// src/components/ScheduleModal/index.js

import React, { useState, useEffect, useContext, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { showSuccessToast } from "../../utils/toastHelper";
import { useHistory } from "react-router-dom";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import {
  Chip,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Typography,
} from "@material-ui/core";
import Autocomplete, {
  createFilterOptions,
} from "@material-ui/lab/Autocomplete";
import moment from "moment";
import { AuthContext } from "../../context/Auth/AuthContext";
import { isArray, capitalize } from "lodash";
import DeleteOutline from "@material-ui/icons/DeleteOutline";
import AttachFile from "@material-ui/icons/AttachFile";
import ConfirmationModal from "../ConfirmationModal";
import MessageVariablesPicker from "../MessageVariablesPicker";
import MaskInput from "../MaskInput";
import useQueues from "../../hooks/useQueues";
import UserStatusIcon from "../UserModal/statusIcon";
import { Facebook, Instagram, WhatsApp, FlashOn } from "@material-ui/icons";
import ColorModeContext from "../../layout/themeContext";
import { createGradientStyles } from "../../utils/themeColors";
import {
  companyLocalDateTimeToUtcIso,
  resolveCompanyTimezone,
  toCompanyInputDateTime
} from "../../utils/companyTimezone";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  // ✅ CORREÇÃO: Estilos do modal seguindo padrão do NewTicketModal
  dialogTitle: {
    padding: theme.spacing(2, 3),
    '& .MuiTypography-root': {
      fontWeight: 600,
      fontSize: '1.2rem'
    }
  },
  ...createGradientStyles(theme),
  dialogContent: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.type === 'dark' ? theme.palette.background.default : '#fafafa',
    overflowY: 'auto',
    overflowX: 'hidden',
    flex: '1 1 auto',
    maxHeight: 'calc(100vh - 200px)',
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      background: theme.palette.type === 'dark' ? '#2a2a2a' : '#f1f1f1',
    },
    '&::-webkit-scrollbar-thumb': {
      background: theme.palette.type === 'dark' ? '#555' : '#888',
      borderRadius: '4px',
      '&:hover': {
        background: theme.palette.type === 'dark' ? '#777' : '#555',
      },
    },
  },
  dialogActions: {
    padding: theme.spacing(2, 3),
    backgroundColor: theme.palette.type === 'dark' ? theme.palette.background.default : '#f5f5f5',
    borderTop: theme.palette.type === 'dark' ? '1px solid #444' : '1px solid #e0e0e0',
    flexShrink: 0,
  },
}));

const ScheduleSchema = Yup.object().shape({
  body: Yup.string().min(5, "Mensagem muito curta").required("Obrigatório"),
  contactId: Yup.number().required("Obrigatório"),
  sendAt: Yup.string().required("Obrigatório"),
  reminderDate: Yup.string().nullable()
});

const ScheduleModal = ({
  open,
  onClose,
  scheduleId,
  contactId,
  initialData,
  cleanContact,
  reload,
  message, // ✅ Nova prop para pre-popular mensagem
  fromMessageInput = false, // ✅ Nova prop para identificar origem
  user
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const colorModeContext = useContext(ColorModeContext);
  const useGradient = colorModeContext?.useGradient || theme.palette?.gradient || false;
  const history = useHistory();
  const isMounted = useRef(true);
  const { companyId } = user;
  const isAdmin = user.profile === 'admin';
  const companyTimezone = resolveCompanyTimezone(
    user?.company?.timezone || localStorage.getItem("companyTimezone")
  );
  const effectiveContactId = contactId || initialData?.contactId || "";
  const isGroupSchedule = Boolean(
    initialData?.isGroup ||
    initialData?.groupJid?.includes("@g.us") ||
    initialData?.contactNumber?.includes("@g.us")
  );

  // ✅ PASSO 3: Valor inicial em local (novo agendamento)
  const getInitialSendAt = () => {
    return moment().tz(companyTimezone).add(1, "hour").format("YYYY-MM-DDTHH:mm");
  };

  const getInitialSignature = () => true;

  const initialState = {
    body: message || "", // ✅ Pre-popular com mensagem se fornecida
    contactId: effectiveContactId, // ✅ Pre-popular com contactId se fornecido
    sendAt: getInitialSendAt(), // ✅ Usar helper para garantir timezone local
    sentAt: "",
    openTicket: "enabled",
    ticketUserId: user.id,
    queueId: initialData?.queueId || "",
    statusTicket: "open", // ✅ Status baseado na origem
    intervalo: 1,
    valorIntervalo: 0,
    enviarQuantasVezes: 1,
    tipoDias: 4,
    assinar: getInitialSignature(),
    // ✅ Novos campos para lembrete
    reminderDate: "",
  };

  const initialContact = {
    id: "",
    name: "",
    channel: "",
  };

  const [schedule, setSchedule] = useState(initialState);
  const [currentContact, setCurrentContact] = useState(initialContact);
  const [contacts, setContacts] = useState([initialContact]);
  const [intervalo, setIntervalo] = useState(1);
  const [tipoDias, setTipoDias] = useState(4);
  const [attachments, setAttachments] = useState([]);
  const attachmentFile = useRef(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const messageInputRef = useRef();
  const [channelFilter, setChannelFilter] = useState("whatsapp");
  const [whatsapps, setWhatsapps] = useState([]);
  const [selectedWhatsapps, setSelectedWhatsapps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [queues, setQueues] = useState([]);
  const [allQueues, setAllQueues] = useState([]);
  const [selectedUser, setSelectedUser] = useState(isAdmin ? user : user);
  const [selectedQueue, setSelectedQueue] = useState(null);
  const { findAll: findAllQueues } = useQueues();
  const [options, setOptions] = useState([]);
  const [searchParam, setSearchParam] = useState("");
  
  // ✅ Parte D: Estado para controlar se agendamento está processado (ENVIADA)
  const [isProcessed, setIsProcessed] = useState(false);
  
  // ✅ 1) Criar flag isReadOnly (só em edição; novo nunca é readonly)
  const isReadOnly = !!scheduleId && isProcessed;

  // Estados para quickMessages
  const [quickMessages, setQuickMessages] = useState([]);
  const [filteredQuickMessages, setFilteredQuickMessages] = useState([]);
  const [loadingQuickMessages, setLoadingQuickMessages] = useState(false);
  const [selectedQuickMessage, setSelectedQuickMessage] = useState("");
  const [quickMessageSearch, setQuickMessageSearch] = useState("");
  const [showAllQuickMessages, setShowAllQuickMessages] = useState(false);
  const [quickMessageMedia, setQuickMessageMedia] = useState(null);

  const getExistingScheduleFiles = (scheduleData = schedule) => {
    if (Array.isArray(scheduleData?.mediaFiles) && scheduleData.mediaFiles.length > 0) {
      return scheduleData.mediaFiles;
    }

    if (scheduleData?.mediaPath && scheduleData?.mediaName) {
      return [{ path: scheduleData.mediaPath, name: scheduleData.mediaName }];
    }

    return [];
  };

  const hasExistingScheduleFiles = getExistingScheduleFiles().length > 0;
  const hasAnyAttachment = attachments.length > 0 || hasExistingScheduleFiles;
  const defaultUserQueueId = Array.isArray(user?.queues) && user.queues.length === 1
    ? user.queues[0].id
    : "";
  const getDefaultQueueIdFromWhatsapp = (whatsapp) => {
    if (!whatsapp) return "";
    if (whatsapp.queueIdImportMessages) return whatsapp.queueIdImportMessages;
    if (Array.isArray(whatsapp.queues) && whatsapp.queues.length === 1) {
      return whatsapp.queues[0].id;
    }
    return "";
  };

  const resolveDefaultQueueId = (userValue = selectedUser, whatsappId = selectedWhatsapps) => {
    if (Array.isArray(userValue?.queues) && userValue.queues.length === 1) {
      return userValue.queues[0].id;
    }

    const selectedWhatsapp = whatsapps.find(
      (item) => String(item.id) === String(whatsappId)
    );

    return getDefaultQueueIdFromWhatsapp(selectedWhatsapp) || "";
  };

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (isMounted.current) {
      const loadQueues = async () => {
        const list = await findAllQueues();
          setAllQueues(list);
          setQueues(list);
          if (!scheduleId && !selectedQueue && defaultUserQueueId) {
            setSelectedQueue(defaultUserQueueId);
          }
        };
        loadQueues();
      }
  }, [defaultUserQueueId, scheduleId, selectedQueue]);

  // Buscar quickMessages quando o modal abrir
  useEffect(() => {
    if (open && user?.companyId) {
      fetchQuickMessages();
    }
  }, [open, user?.companyId, user?.id]);

  // Filtrar respostas rápidas baseado na busca
  useEffect(() => {
    if (quickMessageSearch.trim() === "" && !showAllQuickMessages) {
      setFilteredQuickMessages([]);
    } else if (quickMessageSearch.trim() === "" && showAllQuickMessages) {
      setFilteredQuickMessages(quickMessages);
    } else {
      const filtered = quickMessages.filter(qm => 
        qm.message.toLowerCase().includes(quickMessageSearch.toLowerCase()) ||
        qm.shortcode.toLowerCase().includes(quickMessageSearch.toLowerCase())
      );
      setFilteredQuickMessages(filtered);
    }
  }, [quickMessageSearch, quickMessages, showAllQuickMessages]);

  const fetchQuickMessages = async () => {
    setLoadingQuickMessages(true);
    try {
      console.log("🔍 Buscando quickMessages com params:", {
        companyId: user?.companyId,
        userId: user?.id,
        isOficial: "false"
      });

      const { data } = await api.get("/quick-messages/list", {
        params: {
          companyId: user?.companyId,
          userId: user?.id,
          isOficial: "false"
        }
      });

      console.log("📋 Resposta da API quickMessages:", data);
      setQuickMessages(data || []);
      setFilteredQuickMessages(data || []);
    } catch (err) {
      console.error("❌ Erro ao buscar respostas rápidas:", err);
      toastError(err);
      setQuickMessages([]);
      setFilteredQuickMessages([]);
    } finally {
      setLoadingQuickMessages(false);
    }
  };

  // Função para baixar mídia da quickMessage
  const downloadQuickMessageMedia = async (mediaPath, mediaName, mediaType) => {
    try {
      // console.log("📎 Baixando mídia da quickMessage:", { mediaPath, mediaName, mediaType });

      // Construir URL correta usando a URL base do backend
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080';
      const downloadUrl = `${backendUrl}/public/company${user?.companyId}/quickMessage/${mediaName}`;

      // console.log("🔗 URL de download:", downloadUrl);

      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error(`Erro ao baixar mídia: ${response.status}`);
      }

      const blob = await response.blob();
      const file = new File([blob], mediaName, {
        type: blob.type || getMediaTypeFromExtension(mediaName, mediaType)
      });

      // console.log("✅ Mídia baixada com sucesso:", file);
      return file;
    } catch (err) {
      console.error("❌ Erro ao baixar mídia da quickMessage:", err);
      toastError(err);
      return null;
    }
  };

  // Função auxiliar para determinar o tipo MIME baseado na extensão
  const getMediaTypeFromExtension = (fileName, mediaType) => {
    const extension = fileName.split('.').pop().toLowerCase();

    switch (mediaType) {
      case 'image':
        return `image/${extension === 'jpg' ? 'jpeg' : extension}`;
      case 'audio':
        return `audio/${extension}`;
      case 'video':
        return `video/${extension}`;
      default:
        return 'application/octet-stream';
    }
  };

  useEffect(() => {
    if (searchParam.length < 3) {
      setLoading(false);
      setSelectedQueue("");
      return;
    }
    const delayDebounceFn = setTimeout(() => {
      setLoading(true);
      const fetchUsers = async () => {
        try {
          const { data } = await api.get("/users/");
          setOptions(data.users);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };

      fetchUsers();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam]);

  useEffect(() => {
    api
      .get(`/whatsapp/filter`, {
        params: { session: 0, channel: channelFilter },
      })
      .then(({ data }) => {
        const mappedWhatsapps = data.map((whatsapp) => ({
          ...whatsapp,
          selected: false,
        }));

        setWhatsapps(mappedWhatsapps);
        if (mappedWhatsapps.length && mappedWhatsapps?.length === 1) {
          setSelectedWhatsapps(mappedWhatsapps[0].id);
          if (!scheduleId && !selectedQueue) {
            setSelectedQueue(
              defaultUserQueueId || getDefaultQueueIdFromWhatsapp(mappedWhatsapps[0]) || ""
            );
          }
        }
      });
  }, [currentContact, channelFilter, defaultUserQueueId, scheduleId, selectedQueue]);

  useEffect(() => {
    if (scheduleId || selectedQueue) {
      return;
    }

    const resolvedQueueId = resolveDefaultQueueId();
    if (resolvedQueueId) {
      setSelectedQueue(resolvedQueueId);
    }
  }, [scheduleId, selectedQueue, selectedUser, selectedWhatsapps, whatsapps]);

  useEffect(() => {
    if (contactId && contacts.length) {
      const contact = contacts.find((c) => c.id === contactId);
      if (contact) {
        setCurrentContact(contact);
      }
    }
  }, [contactId, contacts]);

  // ✅ PASSO 1: Logs temporários para identificar scroll
  useEffect(() => {
    if (open) {
      console.log("[MODAL SCROLL] body overflow:", window.getComputedStyle(document.body).overflow);
      console.log("[MODAL SCROLL] html overflow:", window.getComputedStyle(document.documentElement).overflow);
      console.log("[MODAL SCROLL] body scrollHeight:", document.body.scrollHeight);
      console.log("[MODAL SCROLL] window innerHeight:", window.innerHeight);
    }
  }, [open]);

  // ✅ PASSO 2: Travar scroll do body quando modal estiver aberto
  useEffect(() => {
    if (!open) return;

    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyPosition = document.body.style.position;
    const prevBodyTop = document.body.style.top;
    const prevBodyWidth = document.body.style.width;
    
    // Salvar posição atual do scroll
    const scrollY = window.scrollY;
    
    // Bloquear scroll no body e html
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";

    return () => {
      // Restaurar estilos originais
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.position = prevBodyPosition;
      document.body.style.top = prevBodyTop;
      document.body.style.width = prevBodyWidth;
      
      // Restaurar posição do scroll
      window.scrollTo(0, scrollY);
    };
  }, [open]);

  // ✅ MELHORIA: UseEffect otimizado com melhor lógica de inicialização
  useEffect(() => {
    const { companyId } = user;
    if (open) {
      try {
        (async () => {
          // Carregar lista de contatos
          const { data: contactList } = await api.get("/contacts/list", {
            params: { companyId: companyId },
          });

          let customList = contactList.map((c) => ({
            id: c.id,
            name: c.name,
            channel: c.channel,
          }));

          if (isArray(customList)) {
            setContacts([{ id: "", name: "", channel: "" }, ...customList]);
          }

          // ✅ MELHORIA: Lógica de inicialização aprimorada
          if (!scheduleId) {
            // Modal sendo aberto para criar novo agendamento
            // ✅ Garantir que sendAt seja sempre atualizado com data/hora atual do timezone local
            const currentSendAt = moment()
              .tz(companyTimezone)
              .add(1, "hour")
              .format("YYYY-MM-DDTHH:mm");
            
            // ✅ Log para debug (timezone do usuário)
            console.log("[SCHEDULE MODAL] Novo agendamento - timezone local:", {
              userTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              companyTimeZone: companyTimezone,
              sendAtLocal: currentSendAt,
            });
            
            const newScheduleState = {
              ...initialState,
              body: message || "", // ✅ Pre-popular mensagem
              contactId: effectiveContactId, // ✅ Pre-popular contato
              sendAt: currentSendAt, // ✅ Sempre usar data/hora atual do timezone local
              ticketId: initialData?.ticketId || null,
            };

            setIsProcessed(false); // ✅ Novo agendamento sempre editável
            setSchedule(newScheduleState);
            setSelectedUser(user);
            setSelectedQueue(initialData?.queueId || defaultUserQueueId || resolveDefaultQueueId(user, initialData?.whatsappId) || "");
            if (initialData?.whatsappId) {
              setSelectedWhatsapps(initialData.whatsappId);
            }

            // ✅ MELHORIA: Se contactId foi fornecido, definir contato atual
            if (effectiveContactId && customList.length > 0) {
              const foundContact = customList.find((c) => c.id.toString() === effectiveContactId.toString());
              if (foundContact) {
                setCurrentContact(foundContact);
                setChannelFilter(foundContact.channel || "whatsapp");
                console.log("✅ Contato auto-selecionado:", foundContact.name);
              }
            }

            return;
          }

          // ✅ PASSO 4: Carregamento de agendamento existente (sendAt vem com Z)
          // Quando carregar data.sendAt (UTC ISO com Z), preencher o input assim:
          const { data } = await api.get(`/schedules/${scheduleId}`);
          
          // ✅ Parte D: Calcular isProcessed
          const isProcessedValue = data.status === "ENVIADA" || !!data.sentAt;
          setIsProcessed(isProcessedValue);
          
          setSchedule((prevState) => {
            return {
              ...prevState,
              ...data,
              assinar: data.assinar !== false,
              sendAt: toCompanyInputDateTime(data.sendAt, companyTimezone),
              // ✅ Incluir campos de lembrete no carregamento
              reminderDate: data.reminderDate 
                ? toCompanyInputDateTime(data.reminderDate, companyTimezone)
                : "",
            };
          });

          console.log("📅 Agendamento carregado:", data);

          if (data.whatsapp) {
            setSelectedWhatsapps(data.whatsapp.id);
          }

          if (data.ticketUser) {
            setSelectedUser(data.ticketUser);
          } else {
            setSelectedUser(user);
          }

          if (data.queueId) {
            setSelectedQueue(data.queueId);
          } else if (!scheduleId && defaultUserQueueId) {
            setSelectedQueue(defaultUserQueueId);
          }

          if (data.intervalo) {
            setIntervalo(data.intervalo);
          }

          if (data.tipoDias) {
            setTipoDias(data.tipoDias);
          }

          setCurrentContact(data.contact);
        })();
      } catch (err) {
        toastError(err);
      }
    }
  }, [scheduleId, effectiveContactId, open, user, message, fromMessageInput, initialData]);

  const filterOptions = createFilterOptions({
    trim: true,
  });

  const normalizeNullableInt = (value) => {
    if (
      value === undefined ||
      value === null ||
      value === "" ||
      value === "undefined"
    ) {
      return null;
    }

    const parsed = Number(value);
    return Number.isInteger(parsed) ? parsed : null;
  };

  const handleClose = () => {
    onClose();
    setAttachments([]);
    setSchedule({
      ...initialState,
      sendAt: getInitialSendAt(),
      reminderDate: ""
    });
    setIsProcessed(false); // ✅ Reset isProcessed ao fechar
    // ✅ MELHORIA: Reset do contato atual ao fechar
    setCurrentContact(initialContact);
    setSelectedQueue(defaultUserQueueId || resolveDefaultQueueId(user) || "");
    // Reset do dropdown de quickMessages
    setSelectedQuickMessage("");
    setQuickMessageSearch("");
    setShowAllQuickMessages(false);
    setFilteredQuickMessages([]);
    setQuickMessageMedia(null);
    // ✅ Reset dos campos de lembrete
    setSchedule(prevState => ({
      ...prevState,
      reminderDate: "",
    }));
  };

  const handleAttachmentFile = (e) => {
    if (isReadOnly) {
      e.target.value = ""; // Limpar seleção
      return;
    }
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setAttachments((prevState) => [...prevState, ...files]);
    }
    if (attachmentFile.current) {
      attachmentFile.current.value = null;
    }
  };

  const removeLocalAttachment = (indexToRemove) => {
    setAttachments((prevState) => prevState.filter((_, index) => index !== indexToRemove));
  };

  const IconChannel = (channel) => {
    switch (channel) {
      case "facebook":
        return (
          <Facebook style={{ color: "#3b5998", verticalAlign: "middle" }} />
        );
      case "instagram":
        return (
          <Instagram style={{ color: "#e1306c", verticalAlign: "middle" }} />
        );
      case "whatsapp":
        return (
          <WhatsApp style={{ color: "#25d366", verticalAlign: "middle" }} />
        );
      default:
        return "error";
    }
  };

  const renderOption = (option) => {
    if (option.name) {
      return (
        <>
          {IconChannel(option.channel)}
          <Typography
            component="span"
            style={{
              fontSize: 14,
              marginLeft: "10px",
              display: "inline-flex",
              alignItems: "center",
              lineHeight: "2",
            }}
          >
            {option.name}
          </Typography>
        </>
      );
    } else {
      return `${i18n.t("newTicketModal.add")} ${option.name}`;
    }
  };

  const handleSaveSchedule = async (values) => {
    // ✅ 4) Travar submissão no código (segurança)
    if (isReadOnly) {
      console.warn("[SCHEDULE MODAL] bloqueado: agendamento já processado");
      toast.error(i18n.t("scheduleModal.errors.alreadySentReadonly"));
      return;
    }
    
    // ✅ PASSO 5: No submit: converter para UTC ISO antes de enviar
    const resolvedContactId = normalizeNullableInt(
      currentContact?.id || values.contactId || effectiveContactId
    );
    const resolvedWhatsappId = normalizeNullableInt(selectedWhatsapps);
    const resolvedTicketUserId = normalizeNullableInt(
      selectedUser?.id || values.ticketUserId || user.id
    );
    const resolvedQueueId = normalizeNullableInt(selectedQueue);

    if (!resolvedContactId) {
      toast.error(i18n.t("scheduleModal.errors.validContactRequired"));
      return;
    }

    const scheduleData = {
      ...values,
      contactId: resolvedContactId,
      sendAt: companyLocalDateTimeToUtcIso(values.sendAt, companyTimezone),
      reminderDate: values.reminderDate 
        ? companyLocalDateTimeToUtcIso(values.reminderDate, companyTimezone)
        : null, // ✅ Converter para UTC ISO
      userId: user.id,
      whatsappId: resolvedWhatsappId,
      ticketId: normalizeNullableInt(initialData?.ticketId),
      ticketUserId: resolvedTicketUserId,
      queueId: resolvedQueueId,
      intervalo: intervalo || 1,
      tipoDias: tipoDias || 4,
    };

    try {
      if (scheduleId) {
        await api.put(`/schedules/${scheduleId}`, scheduleData);
        if (attachments.length > 0) {
          const formData = new FormData();
          attachments.forEach((attachment) => {
            formData.append("file", attachment);
          });
          await api.post(`/schedules/${scheduleId}/media-upload`, formData);
        }
      } else {
        const { data } = await api.post("/schedules", scheduleData);
        if (attachments.length > 0) {
          const formData = new FormData();
          attachments.forEach((attachment) => {
            formData.append("file", attachment);
          });
          await api.post(`/schedules/${data.schedule.id}/media-upload`, formData);
        }
      }

      showSuccessToast(i18n.t("scheduleModal.success"));

      if (typeof reload == "function") {
        reload();
      }

      if (contactId) {
        if (typeof cleanContact === "function") {
          cleanContact();
          history.push("/schedules");
        }
      }
    } catch (err) {
      toastError(err);
    }

    setCurrentContact(initialContact);
    setSchedule(initialState);
    // ✅ Reset dos campos de lembrete após salvar
    setSchedule(prevState => ({
      ...prevState,
      reminderDate: "",
    }));
    handleClose();
  };

  const handleClickMsgVar = async (msgVar, setValueFunc) => {
    const el = messageInputRef.current;
    const firstHalfText = el.value.substring(0, el.selectionStart);
    const secondHalfText = el.value.substring(el.selectionEnd);
    const newCursorPos = el.selectionStart + msgVar.length;

    setValueFunc("body", `${firstHalfText}${msgVar}${secondHalfText}`);

    await new Promise((r) => setTimeout(r, 100));
    messageInputRef.current.setSelectionRange(newCursorPos, newCursorPos);
  };

  // Função para lidar com seleção de resposta rápida
  const handleQuickMessageSelect = async (selectedMessage, setFieldValue) => {
    console.log("🎯 Mensagem selecionada:", selectedMessage);
    
    if (selectedMessage) {
      console.log("✅ Preenchendo campo body com:", selectedMessage.message);
      setFieldValue("body", selectedMessage.message || "");
      setQuickMessageSearch(selectedMessage.message || "");

      // Se a mensagem tem mídia, baixar e definir como attachment
      if (selectedMessage.mediaPath) {
        console.log("📎 Mensagem com mídia:", selectedMessage.mediaPath);

        try {
          const mediaFile = await downloadQuickMessageMedia(
            selectedMessage.mediaPath,
            selectedMessage.mediaName,
            selectedMessage.mediaType
          );

          if (mediaFile) {
            setAttachments([mediaFile]);
            setQuickMessageMedia({
              path: selectedMessage.mediaPath,
              name: selectedMessage.mediaName,
              type: selectedMessage.mediaType
            });
            console.log("✅ Mídia da quickMessage definida como attachment:", mediaFile);
          }
        } catch (err) {
          console.error("❌ Erro ao processar mídia da quickMessage:", err);
        }
      } else {
        // Limpar mídia anterior se não há mídia na nova seleção
        setQuickMessageMedia(null);
        setAttachments([]);
        if (attachmentFile.current) {
          attachmentFile.current.value = null;
        }
      }
    }
  };

  const deleteMedia = async () => {
    if (attachments.length > 0) {
      setAttachments([]);
      if (attachmentFile.current) {
        attachmentFile.current.value = null;
      }
    }

    // Limpar mídia da quickMessage se existir
    if (quickMessageMedia) {
      setQuickMessageMedia(null);
    }

    if (hasExistingScheduleFiles) {
      await api.delete(`/schedules/${schedule.id}/media-upload`);
      setSchedule((prev) => ({
        ...prev,
        mediaPath: null,
        mediaName: null,
        mediaFiles: [],
      }));
      showSuccessToast(i18n.t("scheduleModal.toasts.deleted"));
      if (typeof reload == "function") {
        console.log(reload);
        console.log("1");
        reload();
      }
    }
  };

  return (
    <div className={classes.root}>
      <ConfirmationModal
        title={i18n.t("scheduleModal.confirmationModal.deleteTitle")}
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={deleteMedia}
      >
        {i18n.t("scheduleModal.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
        disableScrollLock={false}
        PaperProps={{
          style: {
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            margin: '32px auto',
            maxHeight: 'calc(100vh - 64px)',
          },
        }}
      >
        <DialogTitle 
          id="form-dialog-title"
          className={`${classes.dialogTitle} ${useGradient ? classes.gradientBackground : classes.solidBackground}`}
        >
          {schedule.status === "ERRO"
            ? i18n.t("scheduleModal.titles.sendError")
            : i18n.t("scheduleModal.titles.messageStatus", { status: capitalize(schedule.status) })}
          {isGroupSchedule ? ` • ${i18n.t("scheduleModal.titles.group")}: ${initialData?.groupName || initialData?.contactName || i18n.t("scheduleModal.titles.group")}` : ""}
        </DialogTitle>
        <div style={{ display: "none" }}>
          <input
            type="file"
            multiple
            ref={attachmentFile}
            onChange={(e) => handleAttachmentFile(e)}
          />
        </div>
        <Formik
          initialValues={schedule}
          enableReinitialize={true}
          validationSchema={ScheduleSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveSchedule(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ touched, errors, isSubmitting, values, setFieldValue }) => (
            <Form>
              <DialogContent dividers className={classes.dialogContent}>
                {/* ✅ 5) UX — indicar visualmente que está bloqueado */}
                {isReadOnly && (
                  <div style={{
                    padding: "12px 16px",
                    marginBottom: "16px",
                    backgroundColor: "#fff3cd",
                    border: "1px solid #ffc107",
                    borderRadius: "4px",
                    color: "#856404",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}>
                    <span style={{ fontSize: "18px" }}>⚠️</span>
                    <span style={{ fontWeight: 500 }}>
                      {i18n.t("scheduleModal.readonlyNotice")}
                    </span>
                  </div>
                )}
                <Grid container spacing={1}>
                  <Grid item xs={12} md={6} xl={6}>
                    <div className={classes.multFieldLine}>
                      <FormControl variant="outlined" fullWidth>
                        <Autocomplete
                          fullWidth
                          value={currentContact}
                          options={contacts}
                          onChange={(e, contact) => {
                            if (isReadOnly) return; // Bloquear se readonly
                            const contactId = contact ? contact.id : "";
                            setSchedule({ ...schedule, contactId });
                            setFieldValue("contactId", contactId);
                            setCurrentContact(contact ? contact : initialContact);
                            setChannelFilter(
                              contact ? contact.channel : "whatsapp"
                            );
                          }}
                          getOptionLabel={(option) => option.name}
                          renderOption={renderOption}
                          getOptionSelected={(option, value) => {
                            return value.id === option.id;
                          }}
                          disabled={isReadOnly}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              placeholder={i18n.t("scheduleModal.form.contact")}
                              disabled={isReadOnly}
                              InputProps={{
                                ...params.InputProps,
                                readOnly: isReadOnly,
                              }}
                            />
                          )}
                        />
                      </FormControl>
                    </div>
                  </Grid>
                  <Grid item xs={12} md={6} xl={6}>
                    <Field
                      as={TextField}
                      label={i18n.t("scheduleModal.form.sendAt")}
                      type="datetime-local"
                      name="sendAt"
                      error={touched.sendAt && Boolean(errors.sendAt)}
                      helperText={touched.sendAt && errors.sendAt}
                      variant="outlined"
                      fullWidth
                      size="small"
                      style={{ marginTop: "8px" }}
                      disabled={isReadOnly}
                      InputProps={{
                        readOnly: isReadOnly,
                      }}
                    />
                  </Grid>
                </Grid>
                <div className={classes.multFieldLine}>
                  <TextField
                    rows={9}
                    multiline
                    label={i18n.t("scheduleModal.form.body")}
                    name="body"
                    inputRef={messageInputRef}
                    value={values.body || ""}
                    onChange={(e) => {
                      if (isReadOnly) return;
                      setFieldValue("body", e.target.value);
                    }}
                    error={touched.body && Boolean(errors.body)}
                    helperText={touched.body ? errors.body : i18n.t("scheduleModal.form.bodyHelper")}
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    disabled={isReadOnly}
                    InputProps={{
                      readOnly: isReadOnly,
                    }}
                  />
                </div>

                {/* Campo de Busca de Respostas Rápidas */}
                <div className={classes.multFieldLine}>
                  <TextField
                    variant="outlined"
                    fullWidth
                    margin="dense"
                    label={i18n.t("ticketInfo.quickMessages")}
                    placeholder={i18n.t("scheduleModal.form.quickMessageSearch")}
                    value={quickMessageSearch}
                    onChange={(e) => {
                      setQuickMessageSearch(e.target.value);
                      if (e.target.value.trim() === "") {
                        setShowAllQuickMessages(false);
                      }
                    }}
                    onFocus={() => {
                      if (isReadOnly) return;
                      if (quickMessageSearch.trim() === "" && !showAllQuickMessages) {
                        setShowAllQuickMessages(true);
                      }
                    }}
                    onClick={() => {
                      if (isReadOnly) return;
                      if (quickMessageSearch.trim() === "" && !showAllQuickMessages) {
                        setShowAllQuickMessages(true);
                      }
                    }}
                    disabled={loadingQuickMessages || isReadOnly}
                    InputProps={{
                      readOnly: isReadOnly,
                    }}
                    InputProps={{
                      startAdornment: <FlashOn style={{ marginRight: '8px', color: '#1976d2' }} />,
                    }}
                  />
                  
                  {/* Lista de sugestões */}
                  {((quickMessageSearch && filteredQuickMessages.length > 0) || (showAllQuickMessages && filteredQuickMessages.length > 0)) && (
                    <div style={{ 
                      maxHeight: '200px', 
                      overflowY: 'auto', 
                      border: '1px solid #e0e0e0', 
                      borderRadius: '4px', 
                      marginTop: '4px',
                      backgroundColor: 'white',
                      zIndex: 1000,
                      position: 'relative'
                    }}>
                      {filteredQuickMessages.map((quickMessage) => (
                        <div
                          key={quickMessage.id}
                          onClick={() => {
                            if (isReadOnly) return;
                            handleQuickMessageSelect(quickMessage, setFieldValue);
                          }}
                          style={{
                            padding: '12px',
                            cursor: isReadOnly ? 'not-allowed' : 'pointer',
                            borderBottom: '1px solid #f0f0f0',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            opacity: isReadOnly ? 0.6 : 1,
                          }}
                          onMouseEnter={(e) => {
                            if (!isReadOnly) e.target.style.backgroundColor = '#f5f5f5';
                          }}
                          onMouseLeave={(e) => {
                            if (!isReadOnly) e.target.style.backgroundColor = 'white';
                          }}
                        >
                          <FlashOn style={{ fontSize: '16px', color: '#1976d2' }} />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 'bold', fontSize: '14px' }}>
                              {quickMessage.shortcode}
                            </div>
                            <div style={{ 
                              fontSize: '12px', 
                              color: '#666', 
                              maxWidth: '300px', 
                              overflow: 'hidden', 
                              textOverflow: 'ellipsis', 
                              whiteSpace: 'nowrap' 
                            }}>
                              {quickMessage.message}
                            </div>
                          </div>
                          {quickMessage.mediaPath && (
                            <AttachFile style={{ fontSize: '14px', color: '#666' }} />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Mensagem quando não há resultados */}
                  {quickMessageSearch && filteredQuickMessages.length === 0 && !loadingQuickMessages && (
                    <div style={{ 
                      padding: '12px', 
                      textAlign: 'center', 
                      color: '#666', 
                      fontStyle: 'italic',
                      border: '1px solid #e0e0e0', 
                      borderRadius: '4px', 
                      marginTop: '4px'
                    }}>
                      {i18n.t("scheduleModal.form.noQuickMessages")}
                    </div>
                  )}
                </div>

                <Grid item xs={12} md={12} xl={12}>
                  <MessageVariablesPicker
                    disabled={isSubmitting || isReadOnly}
                    showSchedulingVars={true}
                    onClick={(value) => {
                      if (isReadOnly) return;
                      handleClickMsgVar(value, setFieldValue);
                    }}
                  />
                </Grid>
                <Grid container spacing={1}>
                  <Grid item xs={12} md={6} xl={3}>
                    <FormControl
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      className={classes.formControl}
                    >
                      <InputLabel id="whatsapp-selection-label">
                        {i18n.t("campaigns.dialog.form.whatsapp")}
                      </InputLabel>
                      <Field
                        as={Select}
                        label={i18n.t("campaigns.dialog.form.whatsapp")}
                        placeholder={i18n.t("campaigns.dialog.form.whatsapp")}
                        labelId="whatsapp-selection-label"
                        id="whatsappIds"
                        name="whatsappIds"
                        required
                        error={touched.whatsappId && Boolean(errors.whatsappId)}
                        value={selectedWhatsapps}
                        disabled={isReadOnly}
                        onChange={(event) => {
                          if (isReadOnly) return;
                          setSelectedWhatsapps(event.target.value);
                          if (!selectedQueue) {
                            const selectedWhatsapp = whatsapps.find(
                              (item) => String(item.id) === String(event.target.value)
                            );
                            const resolvedQueueId =
                              (Array.isArray(selectedUser?.queues) && selectedUser.queues.length === 1
                                ? selectedUser.queues[0].id
                                : "") || getDefaultQueueIdFromWhatsapp(selectedWhatsapp);
                            if (resolvedQueueId) {
                              setSelectedQueue(resolvedQueueId);
                            }
                          }
                        }}
                      >
                        {whatsapps &&
                          whatsapps.map((whatsapp) => (
                            <MenuItem key={whatsapp.id} value={whatsapp.id}>
                              {whatsapp.name}
                            </MenuItem>
                          ))}
                      </Field>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={12} xl={3}>
                    <FormControl
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      className={classes.formControl}
                    >
                      <InputLabel id="openTicket-selection-label">
                        {i18n.t("campaigns.dialog.form.openTicket")}
                      </InputLabel>
                      <Field
                        as={Select}
                        label={i18n.t("campaigns.dialog.form.openTicket")}
                        placeholder={i18n.t("campaigns.dialog.form.openTicket")}
                        labelId="openTicket-selection-label"
                        id="openTicket"
                        name="openTicket"
                        error={touched.openTicket && Boolean(errors.openTicket)}
                        disabled={isReadOnly}
                      >
                        <MenuItem value={"enabled"}>
                          {i18n.t("campaigns.dialog.form.enabledOpenTicket")}
                        </MenuItem>
                        <MenuItem value={"disabled"}>
                          {i18n.t("campaigns.dialog.form.disabledOpenTicket")}
                        </MenuItem>
                      </Field>
                    </FormControl>
                  </Grid>
                </Grid>
                <Grid spacing={1} container>
                  <Grid item xs={12} md={6} xl={4}>
                    <Autocomplete
                      style={{ marginTop: "8px" }}
                      variant="outlined"
                      margin="dense"
                      className={classes.formControl}
                      getOptionLabel={(option) => `${option.name}`}
                      value={isAdmin ? selectedUser : user}
                      size="small"
                      onChange={(e, newValue) => {
                        if (isReadOnly) return;
                        if (isAdmin) {
                          setSelectedUser(newValue);
                          if (newValue != null && Array.isArray(newValue.queues)) {
                            if (newValue.queues.length === 1) {
                              setSelectedQueue(newValue.queues[0].id);
                            } else if (!selectedQueue) {
                              const resolvedQueueId = resolveDefaultQueueId(newValue);
                              if (resolvedQueueId) {
                                setSelectedQueue(resolvedQueueId);
                              }
                            }
                            setQueues(newValue.queues);
                          } else {
                            setQueues(allQueues);
                            setSelectedQueue(resolveDefaultQueueId(null) || "");
                          }
                        }
                      }}
                      options={isAdmin ? options : [user]}
                      filterOptions={filterOptions}
                      freeSolo={isAdmin}
                      fullWidth
                      disabled={isReadOnly || values.openTicket === "disabled" || !isAdmin}
                      autoHighlight
                      noOptionsText={i18n.t("transferTicketModal.noOptions")}
                      loading={loading}
                      renderOption={(option) => (
                        <span>
                          {" "}
                          <UserStatusIcon user={option} /> {option.name}
                        </span>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={i18n.t("transferTicketModal.fieldLabel")}
                          variant="outlined"
                          onChange={isAdmin ? (e) => setSearchParam(e.target.value) : undefined} // Só busca se for admin
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <React.Fragment>
                                {loading ? (
                                  <CircularProgress color="inherit" size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </React.Fragment>
                            ),
                            readOnly: isReadOnly || !isAdmin, // Modo somente leitura se readonly ou não for admin
                          }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={6} xl={6}>
                    <FormControl
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      className={classes.formControl}
                    >
                      <InputLabel>
                        {i18n.t("transferTicketModal.fieldQueueLabel")}
                      </InputLabel>
                      <Select
                        value={selectedQueue}
                        onChange={(e) => {
                          if (isReadOnly) return;
                          setSelectedQueue(e.target.value);
                        }}
                        label={i18n.t(
                          "transferTicketModal.fieldQueuePlaceholder"
                        )}
                        disabled={isReadOnly || values.openTicket === "disabled"}
                      >
                        {queues.map((queue) => (
                          <MenuItem key={queue.id} value={queue.id}>
                            {queue.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
                <Grid spacing={1} container style={{ marginTop: "-10px" }}>
                  <Grid item xs={12} md={6} xl={6}>
                    <FormControl
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      className={classes.formControl}
                    >
                      <InputLabel id="statusTicket-selection-label">
                        {i18n.t("campaigns.dialog.form.statusTicket")}
                      </InputLabel>
                      <Field
                        as={Select}
                        disabled={isReadOnly || values.openTicket === "disabled"}
                        label={i18n.t("campaigns.dialog.form.statusTicket")}
                        placeholder={i18n.t(
                          "campaigns.dialog.form.statusTicket"
                        )}
                        labelId="statusTicket-selection-label"
                        id="statusTicket"
                        name="statusTicket"
                        error={
                          touched.statusTicket && Boolean(errors.statusTicket)
                        }
                      >
                        <MenuItem value={"closed"}>
                          {i18n.t("campaigns.dialog.form.closedTicketStatus")}
                        </MenuItem>
                        <MenuItem value={"open"}>
                          {i18n.t("campaigns.dialog.form.openTicketStatus")}
                        </MenuItem>
                      </Field>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6} xl={6}>
                    <FormControlLabel
                      control={
                        <Field
                          as={Switch}
                          color="primary"
                          name="assinar"
                          checked={values.assinar}
                          disabled={isReadOnly || values.openTicket === "disabled"}
                        />
                      }
                      label={i18n.t("scheduleModal.form.assinar")}
                    />
                  </Grid>
                </Grid>
                <br />
                <Grid container spacing={1}>
                  {/* Seção de Lembrete */}
                  <h3>{i18n.t("scheduleModal.reminder.title")}</h3>
                  <p>{i18n.t("scheduleModal.reminder.description")}</p>
                  <br />
                  <Grid container spacing={1}>
                    <Grid item xs={12} md={6} xl={6}>
                      <Field
                        as={TextField}
                        label={i18n.t("scheduleModal.reminder.date")}
                        type="datetime-local"
                        name="reminderDate"
                        error={touched.reminderDate && Boolean(errors.reminderDate)}
                        helperText={touched.reminderDate && errors.reminderDate}
                        variant="outlined"
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        disabled={isReadOnly}
                        InputProps={{
                          readOnly: isReadOnly,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6} xl={6}>
                      <Typography variant="body2" color="textSecondary" style={{ padding: '16px', backgroundColor: '#f5f5f5', borderRadius: '4px', marginTop: '8px' }}>
                        {i18n.t("scheduleModal.reminder.sameMessage")}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
                <br />

                <h3>{i18n.t("recurrenceSection.title")}</h3>
                <p>{i18n.t("recurrenceSection.description")}</p>
                <br />
                <Grid container spacing={1}>
                  <Grid item xs={12} md={4} xl={4}>
                    <FormControl size="small" fullWidth variant="outlined">
                      <InputLabel id="demo-simple-select-label">
                        {i18n.t("recurrenceSection.labelInterval")}
                      </InputLabel>
                      <Select
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        value={intervalo}
                        onChange={(e) => {
                          if (isReadOnly) return;
                          setIntervalo(e.target.value || 1);
                        }}
                        label={i18n.t("recurrenceSection.labelInterval")}
                        disabled={isReadOnly}
                      >
                        <MenuItem value={1}>
                          {i18n.t("recurrenceSection.options.days")}
                        </MenuItem>
                        <MenuItem value={2}>
                          {i18n.t("recurrenceSection.options.weeks")}
                        </MenuItem>
                        <MenuItem value={3}>
                          {i18n.t("recurrenceSection.options.months")}
                        </MenuItem>
                        <MenuItem value={4}>
                          {i18n.t("recurrenceSection.options.minutes")}
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={4} xl={4}>
                    <Field
                      as={TextField}
                      label={i18n.t("recurrenceSection.intervalFilterValue")}
                      name="valorIntervalo"
                      size="small"
                      error={
                        touched.valorIntervalo && Boolean(errors.valorIntervalo)
                      }
                      InputLabelProps={{ shrink: true }}
                      variant="outlined"
                      fullWidth
                      disabled={isReadOnly}
                      InputProps={{
                        readOnly: isReadOnly,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4} xl={4}>
                    <Field
                      as={TextField}
                      label={i18n.t("recurrenceSection.sendAsManyTimes")}
                      name="enviarQuantasVezes"
                      size="small"
                      error={
                        touched.enviarQuantasVezes &&
                        Boolean(errors.enviarQuantasVezes)
                      }
                      variant="outlined"
                      fullWidth
                      disabled={isReadOnly}
                      InputProps={{
                        readOnly: isReadOnly,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={12} xl={12}>
                    <FormControl size="small" fullWidth variant="outlined">
                      <InputLabel id="demo-simple-select-label">
                        {i18n.t("recurrenceSection.sendAsManyTimes")}
                      </InputLabel>
                      <Select
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        value={tipoDias}
                        onChange={(e) => {
                          if (isReadOnly) return;
                          setTipoDias(e.target.value || 4);
                        }}
                        label={i18n.t("recurrenceSection.sendAsManyTimes")}
                        disabled={isReadOnly}
                      >
                        <MenuItem value={4}>
                          {i18n.t(
                            "recurrenceSection.shipNormallyOnNonbusinessDays"
                          )}
                        </MenuItem>
                        <MenuItem value={5}>
                          {i18n.t("recurrenceSection.sendOneBusinessDayBefore")}
                        </MenuItem>
                        <MenuItem value={6}>
                          {" "}
                          {i18n.t("recurrenceSection.sendOneBusinessDayLater")}
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
                {hasAnyAttachment && (
                  <Grid xs={12} item>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {attachments.map((attachment, index) => (
                        <div key={`${attachment.name}-${index}`} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Button startIcon={<AttachFile />}>
                            {attachment.name}
                          </Button>
                          {quickMessageMedia && attachment.name === quickMessageMedia.name && (
                            <Chip
                              label={i18n.t("scheduleModal.quickMessageAttachment")}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          )}
                          {!isReadOnly && (
                            <IconButton
                              onClick={() => removeLocalAttachment(index)}
                              color="secondary"
                            >
                              <DeleteOutline color="secondary" />
                            </IconButton>
                          )}
                        </div>
                      ))}

                      {getExistingScheduleFiles().map((file, index) => (
                        <div key={`${file.path || file.name}-${index}`} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Button startIcon={<AttachFile />}>
                            {file.name}
                          </Button>
                        </div>
                      ))}

                      {hasExistingScheduleFiles && !isReadOnly && (
                        <div>
                          <IconButton
                            onClick={() => {
                              setConfirmationOpen(true);
                            }}
                            color="secondary"
                          >
                            <DeleteOutline color="secondary" />
                          </IconButton>
                        </div>
                      )}
                    </div>
                  </Grid>
                )}
              </DialogContent>
              <DialogActions className={classes.dialogActions}>
                <Button
                  color="primary"
                  onClick={() => {
                    if (isReadOnly) return;
                    attachmentFile.current.click();
                  }}
                  disabled={isSubmitting || isReadOnly}
                  variant="outlined"
                >
                  {attachments.length > 0 || hasExistingScheduleFiles
                    ? "Adicionar mais arquivos"
                    : i18n.t("quickMessages.buttons.attach")}
                </Button>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  disabled={isSubmitting}
                  variant="outlined"
                >
                  {i18n.t("scheduleModal.buttons.cancel")}
                </Button>
                {!isReadOnly && (
                  <Button
                    type="submit"
                    color="primary"
                    disabled={isSubmitting || isReadOnly}
                    variant="contained"
                    className={classes.btnWrapper}
                  >
                    {scheduleId
                      ? `${i18n.t("scheduleModal.buttons.okEdit")}`
                      : `${i18n.t("scheduleModal.buttons.okAdd")}`}
                    {isSubmitting && (
                      <CircularProgress
                        size={24}
                        className={classes.buttonProgress}
                      />
                    )}
                  </Button>
                )}
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default ScheduleModal;
